import React from 'react';
import { BrowserRouter, useLocation, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Global, css } from '@emotion/react';
import {
  palette,
  radius,
  shadow,
  typography,
  zIndex,
} from './style-utils/theme';
import { AppRouter } from './components/app-router/app-router';
import { AuthProvider, useAuth } from './context/auth-provider';
import { InstallPrompt } from './components/install-prompt/install-prompt';
import { IqChat } from './components/iq/iq-chat';
import { IqChatProvider } from './components/iq/iq-chat-context';
import { HandoffProvider } from './components/iq/handoff-context';
import { PublicRoutes } from './components/onboarding/public-routes';
// ONE provider for the whole authenticated app, and therefore ONE WebSocket.
// The bell appears in the staff rail and again on the resident dashboard; both
// are views of this state. Mounting it per-surface would open a socket per
// surface and spend the server's per-user connection cap on tabs nobody is
// looking at, as well as double-counting the badge.
//
// Inside RootRouter (so it is under AuthProvider and can read authStatus) and
// ABOVE AppRouter, so the connection survives navigation instead of being torn
// down and re-established on every route change.
import { NotificationsProvider } from './components/notifications/notifications-context';
import { NotificationDock } from './components/notifications/notification-dock';
// The clock, asked once for the whole app: the card renders it and the main
// menu withholds the controls that record work until it says yes. Two readers
// of one fact — so the fact lives above both of them.
import { ShiftProvider } from './components/shifts/shift-context';
import {
  isPublicPath,
  rendersWhenSignedIn,
  resolvePostAuthRoute,
} from './utils/post-auth-route';
import { BrandedLoader } from './components/loader/branded-loader';
import { SkipLink } from './components/layout/skip-link';
import { ErrorBoundary } from './components/layout/error-boundary';
import { OrgSuspended } from './components/layout/org-suspended';
import { AccountClosing } from './components/layout/account-closing';
import { MotionRoot } from './motion/motion-root';
import { Presence } from './motion/presence';

// Public routes render OUTSIDE AppRouter (whose data hooks would fire on every
// render). This wrapper also handles a few cross-cutting routing rules.
const RootRouter: React.FC = () => {
  const { pathname } = useLocation();
  const {
    user,
    onboarding,
    permissions,
    authStatus,
    orgSuspended,
    accountClosing,
  } = useAuth();
  const publicPath = isPublicPath(pathname);
  const booting = authStatus === 'loading';

  // The token is httpOnly now, so the client can't read a cookie to guess
  // whether a session exists — it has to wait for /auth to answer. Rendering the
  // shell during that probe would flash the dashboard at a logged-out visitor;
  // redirecting would eject a signed-in user on every reload.
  //
  // THE LOADER IS AN OVERLAY, NOT A BRANCH. It sits in its own <Presence>
  // above whichever tree the probe resolves to, so when /auth answers the
  // loader fades OUT over the sign-in card or the dashboard that has just
  // mounted beneath it. Nothing renders under it while booting — that part of
  // the guarantee is unchanged — but the handover is a crossfade, not a cut.
  let body: React.ReactNode = null;

  // #15 — an authenticated user on a public auth page (/signup) is bounced home
  // so they can't clobber their own session.
  //
  // NOT the pages in SIGNED_IN_PUBLIC_PREFIXES. An invite link and a
  // password-reset link are held by someone who is not necessarily the browser's
  // current occupant, and this bounce is what silently broke every invitation:
  // the admin who sent it (or anyone else already signed in on that machine)
  // landed on their own dashboard instead of the accept form, so the password
  // could never be set. Those pages end the incumbent session themselves before
  // minting the new one.
  //
  // /verify-email is in that set for the OPPOSITE reason and was the same bug
  // left behind: the person clicking a verify link is almost always the signed-in
  // occupant, so this bounce fired every time and the page never mounted — the
  // token was never consumed and the account stayed unverified permanently. It
  // does not end the session; it re-reads it. See post-auth-route.ts.
  // The organisation is suspended: one screen that says so, in place of the
  // whole signed-in app (and of the sign-in form a reload would otherwise fall
  // through to). Public pages — a guest's pass, the legal pages, a password
  // reset — are nobody's session and stay as they are.
  if (!booting && orgSuspended && !publicPath) {
    body = <OrgSuspended />;
  } else if (!booting && accountClosing && !publicPath) {
    // Independent Stays IS8: the owner closed the account (frozen).
    body = <AccountClosing />;
  } else if (!booting && publicPath && user && !rendersWhenSignedIn(pathname)) {
    body = (
      <Navigate
        to={resolvePostAuthRoute(user, onboarding, permissions)}
        replace
      />
    );
  } else if (!booting && publicPath) {
    body = <PublicRoutes />;
  } else if (!booting && authStatus === 'anon' && pathname !== '/') {
    // #26 — a signed-out visitor deep-linking a protected path goes straight to
    // login ('/'), instead of flashing the dashboard shell for a frame.
    body = <Navigate to="/" replace />;
  } else if (!booting) {
    body = (
    <NotificationsProvider>
      <IqChatProvider>
      {/* D-25 — the shift handover inbox. OUTSIDE <IqChat> and ABOVE
          <AppRouter>, because two surfaces have to agree about it: the badge on
          the IQ entry point and the line on the shift clock at the top of the
          main menu. It self-suppresses for anybody without `shift:clock` and
          issues no request for them.

          It is NOT gated on the IQ entitlement, unlike <IqChat> below. A
          handover is written on the ordinary shift permissions and degrades to
          the deterministic floor when the model is unavailable, so an account
          that never bought Lobbero IQ can still hold real handovers — and the
          shift clock is the surface that delivers them to those readers. */}
      <HandoffProvider>
        <ShiftProvider>
        {/* FIRST IN THE TAB ORDER, on every authenticated screen. Every one of
            them puts a rail, a header and often a step list before its content;
            without this, reaching the page by keyboard costs a dozen Tab
            presses after every navigation. Its target is the <main> that
            ContentWrapper now renders. */}
        <SkipLink />
        <AppRouter />
        {/* The notification bell, pinned top-right on every screen. Mounted
            here rather than inside the staff rail and the resident header —
            which is where it used to live, twice, in two different corners.
            Together with <IqChat> below it bookends the right edge: bell top,
            IQ bottom, everywhere. */}
        {user ? <NotificationDock /> : null}
        {/* #28 — the PWA install banner only for signed-in users on app routes. */}
        {user ? <InstallPrompt /> : null}
        {/* Lobbero IQ — the persistent AI entry point (SPEC §7.1), now a floating
            modal chat with a full-screen mode. Self-suppresses on entitlement /
            permission / onboarding state, so it is invisible to anyone who cannot
            use it. Inside the provider so the "Shift report" menu tile opens this
            same instance. */}
        {user ? <IqChat /> : null}
        </ShiftProvider>
      </HandoffProvider>
      </IqChatProvider>
    </NotificationsProvider>
    );
  }

  return (
    <>
      <Presence>
        {booting ? (
          <BrandedLoader key="boot" label="Preparing your concierge desk…" />
        ) : null}
      </Presence>
      {body}
    </>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <AuthProvider>
        <MotionRoot>
        {/* ── THE APP HAD NO ERROR BOUNDARY AT ALL ──
            `componentDidCatch` / `getDerivedStateFromError` appeared nowhere in
            src, so a single render-time throw anywhere below here unmounted the
            whole tree and left a white screen: no message, no way back, nothing
            to report. On a front desk mid-shift that is the desk going dark.

            INSIDE AuthProvider, so a crash does not take the session with it and
            "Try again" re-mounts into a signed-in app rather than the sign-in
            card. Above RootRouter, so it covers the public routes too.

            The Toaster stays OUTSIDE it: toasts are how a failure is reported,
            and a boundary that could take the reporter with it is worse than
            none. See error-boundary.tsx for what a boundary cannot catch, and
            for why the reported `reportAllChanges` crash is not ours. */}
        <ErrorBoundary>
          <RootRouter />
        </ErrorBoundary>
        {/* Twenty-two files call toast.success / toast.error. Until this was
            mounted, react-hot-toast had nowhere to render and dropped every one
            of them on the floor — so a failed save (a unit limit, a validation
            409, a dead network) looked to the user like the button did nothing.
            Mounted at the App root, outside RootRouter's branches, so it also
            survives the loading and anonymous states where login errors fire.

            aria-live is what makes a toast reach a screen reader; errors hold
            longer than confirmations because they are the ones worth reading. */}
        {/* Dead centre of the viewport.
            react-hot-toast has no "center" position — "top-center" only centres
            horizontally, and every toast is an ABSOLUTELY positioned child
            pinned to the container's top edge, so the container collapses to
            zero height and a transform on it does nothing. Pinning that edge to
            50% leaves the toast half its own height too low.

            So the toasts are taken out of absolute positioning and the container
            is made a centring flex box over the whole viewport. `position` is
            the only thing overridden — the library's inline `transform` is left
            alone, because that is what it uses to offset a SECOND toast, and
            forcing it would stack them all on the same pixel. In normal flow
            they now stack downwards from the middle instead. */}
        <Global
          styles={css`
            .lobbero-toast-center > * {
              position: static !important;
            }
          `}
        />
        <Toaster
          position="top-center"
          containerClassName="lobbero-toast-center"
          containerStyle={{
            zIndex: zIndex.toast,
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // NEAR THE TOP, NOT OVER THE MIDDLE OF THE WORK.
            //
            // Centred, a toast sits on the thing that just failed — and while it
            // holds, the message about the form covers the form. Stacked (which
            // repeated failures did before utils/toast gave them identity) it
            // covered the screen. Anchoring to the top keeps the report in view
            // and the work visible under it.
            justifyContent: 'flex-start',
            paddingTop: '24px',
          }}
          toastOptions={{
            duration: 3500,
            style: {
              // Centred toasts sit on top of whatever the user is working on,
              // and react-hot-toast gives the bar pointer-events:auto so it can
              // be clicked to dismiss. Together that means the middle of every
              // screen stops accepting input while a toast is up — a form field
              // under it silently ignores clicks. Toasts here dismiss on a timer
              // (8s for errors), so nothing is lost by making them see-through
              // to the pointer, and a whole class of "the app froze" is.
              pointerEvents: 'none',
              maxWidth: '420px',
              padding: '12px 16px',
              borderRadius: radius.sm,
              background: palette.surface,
              color: palette.ink,
              border: `1px solid ${palette.line}`,
              boxShadow: shadow.md,
              fontFamily: typography.ui,
              fontSize: `${typography.size.sm}px`,
              lineHeight: typography.leading.normal,
            },
            success: {
              iconTheme: { primary: palette.success, secondary: palette.white },
            },
            error: {
              // Long enough to read a sentence twice, short enough that the
              // NEXT thing you do is not done underneath the last failure. Eight
              // seconds was the old value and it is most of a minute across
              // three failed attempts.
              duration: 5000,
              iconTheme: { primary: palette.alert, secondary: palette.white },
            },
          }}
        />
        </MotionRoot>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
